import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { ShoppingBag, Coins, Gift, Clock, CheckCircle2, Package, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { fireBigConfetti } from "../lib/confetti";
import { useAuth } from "../context/AuthContext";

export default function Store() {
  const { user } = useAuth();
  const [coins, setCoins] = useState({ earned: 0, spent: 0, balance: 0 });
  const [rewards, setRewards] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const isObserver = user?.role === "observer";

  const load = async () => {
    try {
      const [c, r, h] = await Promise.all([
        api.get("/me/coins"),
        api.get("/rewards"),
        api.get("/redemptions"),
      ]);
      setCoins(c.data);
      setRewards(r.data);
      setHistory(h.data);
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const redeem = async (reward) => {
    if (isObserver) {
      toast.error("Observador não resgata prêmios");
      return;
    }
    if (coins.balance < reward.cost) {
      toast.error(`Faltam ${reward.cost - coins.balance} 🪙`);
      return;
    }
    if (!window.confirm(`Resgatar "${reward.name}" por ${reward.cost} 🪙?\n\nSaldo atual: ${coins.balance} 🪙`)) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/rewards/${reward.id}/redeem`);
      fireBigConfetti();
      toast.success(`🎁 "${reward.name}" resgatado! Fale com seu admin pra receber.`);
      setCoins(data.coins);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const nextCoinXp = 100 - (((user?.xp || 0) % 100));

  return (
    <div className="arcade-grid min-h-screen pb-28">
      <div className="phone-frame phone-frame-full px-4 pt-6">
        <div className="mb-4">
          <p className="chip chip-gold mb-2">🛍️ Loja de recompensas</p>
          <h1 className="font-arcade text-3xl font-extrabold text-white tracking-tight">
            Troque XP por brindes
          </h1>
          <p className="text-sm text-slate-400 mt-1">A cada 100 XP = 1 🪙 moeda</p>
        </div>

        {/* Coin balance card */}
        <div className="neon-card-gold p-5 mb-5 bounce-in">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="chip chip-gold mb-1">⚡ Seu saldo</p>
              <div className="flex items-baseline gap-2">
                <span className="font-arcade text-5xl font-extrabold text-white" data-testid="coin-balance">
                  {coins.balance}
                </span>
                <span className="font-arcade text-2xl text-amber-300">🪙</span>
              </div>
              <p className="text-[11px] font-mono text-slate-300 mt-1">
                Ganhas: {coins.earned} • Gastas: {coins.spent}
              </p>
            </div>
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-pink-500 flex items-center justify-center badge-pulse shadow-xl">
              <Coins size={30} className="text-white" strokeWidth={2.4} />
            </div>
          </div>
          <div>
            <div className="flex justify-between text-[11px] font-mono text-slate-300 mb-1">
              <span>Progresso pra próxima moeda</span>
              <span>{100 - nextCoinXp}/100 XP</span>
            </div>
            <div className="progress-track">
              <div className="progress-fill" style={{ width: `${100 - nextCoinXp}%` }} />
            </div>
          </div>
        </div>

        {/* Rewards grid */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-arcade text-lg font-bold text-white flex items-center gap-2">
            <ShoppingBag size={18} className="text-fuchsia-400" />
            Prêmios disponíveis
          </h2>
          <span className="text-[11px] font-mono text-slate-400 uppercase tracking-widest">
            {rewards.length} itens
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-6" data-testid="rewards-grid">
          {rewards.length === 0 && (
            <p className="col-span-2 text-center text-slate-500 py-8 text-sm">
              Admin ainda não cadastrou brindes.
            </p>
          )}
          {rewards.map((r) => {
            const canAfford = coins.balance >= r.cost;
            const outOfStock = r.stock <= 0;
            return (
              <div key={r.id} className="neon-card overflow-hidden flex flex-col" data-testid={`reward-card-${r.id}`}>
                <div className="aspect-square bg-slate-800 relative overflow-hidden">
                  {r.image_url ? (
                    <img src={r.image_url} alt={r.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20">
                      <Gift size={40} className="text-fuchsia-400/50" />
                    </div>
                  )}
                  {outOfStock && (
                    <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                      <span className="chip" style={{background:"rgba(239,68,68,0.3)",color:"#fca5a5",borderColor:"rgba(239,68,68,0.5)"}}>
                        ESGOTADO
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-3 flex flex-col flex-1">
                  <p className="font-arcade text-sm font-bold text-white line-clamp-1">{r.name}</p>
                  {r.description && (
                    <p className="text-[11px] text-slate-400 line-clamp-2 mt-0.5">{r.description}</p>
                  )}
                  <div className="flex items-baseline gap-1 mt-2">
                    <span className="font-arcade text-xl font-bold text-amber-300">{r.cost}</span>
                    <span className="text-sm">🪙</span>
                  </div>
                  <p className="text-[10px] font-mono text-slate-500">Estoque: {r.stock}</p>
                  <button
                    onClick={() => redeem(r)}
                    disabled={loading || outOfStock || !canAfford || isObserver}
                    data-testid={`redeem-btn-${r.id}`}
                    className="btn-gold mt-2 py-2 text-xs flex items-center justify-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Sparkles size={14} />
                    {isObserver ? "Observador" : outOfStock ? "Esgotado" : canAfford ? "Resgatar" : "Saldo baixo"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* History */}
        <h2 className="font-arcade text-lg font-bold text-white flex items-center gap-2 mb-3">
          <Package size={18} className="text-cyan-400" />
          Meus resgates
        </h2>
        <div className="space-y-2" data-testid="redemption-history">
          {history.length === 0 && (
            <p className="text-center text-slate-500 py-6 text-sm">Nenhum resgate ainda</p>
          )}
          {history.map((h) => (
            <div key={h.id} className="neon-card p-3 flex items-center gap-3" data-testid="redemption-item">
              <div className="w-10 h-10 rounded-lg bg-slate-800 overflow-hidden shrink-0 flex items-center justify-center">
                {h.reward_image ? (
                  <img src={h.reward_image} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Gift size={18} className="text-fuchsia-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-arcade text-sm font-bold text-white truncate">{h.reward_name}</p>
                <p className="text-[11px] text-slate-400">
                  {new Date(h.created_at).toLocaleString("pt-BR")}
                </p>
              </div>
              <div className="text-right whitespace-nowrap">
                <p className="font-arcade text-sm font-bold text-amber-300">-{h.cost} 🪙</p>
                {h.status === "delivered" ? (
                  <span className="chip chip-green text-[10px]">
                    <CheckCircle2 size={10} /> Entregue
                  </span>
                ) : (
                  <span className="chip text-[10px]">
                    <Clock size={10} /> Pendente
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
